import { FCWithChildren } from '@types';
import React from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, ModalBody, Input, Label, HelperText, TermsLink } from '@components';
import { useForm } from 'react-hook-form';
import useLayoutContext from '@hooks/useLayoutContext';
import { IVerificationFormData } from '@pages/VerificationRegister/VerificationRegister';

type ModalVerificationProps = {
  deviceConflict?: boolean;
  error?: any;
  loading?: boolean;
  invalidEmail?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (formData: IVerificationFormData) => void;
};

const ModalVerificationRegister: FCWithChildren<ModalVerificationProps> = ({
  deviceConflict,
  error,
  invalidEmail,
  isOpen,
  loading,
  onClose,
  onConfirm,
}) => {
  const { emailRestrictionType } = useLayoutContext();

  const milOnly = emailRestrictionType === 'mil_only';

  const VERIFICATION_SCHEMA = yup.object().shape({
    email: yup
      .string()
      .email('Must be a valid Email Address')
      .required('Email Address required')
      .test(
        'military-email',
        'Email must end with .mil',
        (value) => !milOnly || (value ? value.toLowerCase().endsWith('.mil') : false),
      ),
    termsAndConditions: yup.boolean().oneOf([true], 'You must accept the Terms and Conditions'),
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<IVerificationFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(VERIFICATION_SCHEMA),
  });

  const isValid = (input: string, isController: boolean = false): boolean =>
    isController ? !(errors as any)[input]?.value?.message : !(errors as any)[input]?.message;

  return (
    <Modal closeModal={onClose} isOpen={isOpen} closeOnEscape={false}>
      <form>
        <ModalBody>
          <div className="mb-6">
            <h1 className="text-3xl antialiased font-normal">Email Verification</h1>
          </div>
          <Label className="my-4">
            <span>
              Email Address<span className="text-red-600">*</span>
            </span>
            <Input
              className="my-2"
              {...register('email')}
              placeholder="Email Address"
              type="email"
              onChange={(e) =>
                setValue('email', e.target.value.toLowerCase(), { shouldValidate: true })
              }
              {...(isValid('email') ? {} : { valid: false })}
            />
            <HelperText valid={isValid('email')}>{errors.email?.message}</HelperText>
          </Label>
          <Label check>
            <Input
              {...register('termsAndConditions')}
              type="checkbox"
              {...(isValid('termsAndConditions') ? {} : { valid: false })}
            />
            <p className="px-2 text-xs-left">
              Check box to indicate you agree to the{' '}
              <TermsLink className="underline border-2 border-transparent focus:border-blue-700" />
            </p>
          </Label>
          <HelperText valid={isValid('termsAndConditions')} className="flex">
            {errors.termsAndConditions?.message}
          </HelperText>
          {!!error && (
            <HelperText valid={false} className="flex">
              {invalidEmail
                ? 'The email you provided was invalid.'
                : deviceConflict
                ? 'This device is already registered, please go back and click Forgot Username or Password if needed.'
                : 'There was a problem setting up your account. Please try again.'}
            </HelperText>
          )}
          <div className="flex flex-col w-full mt-5 divide-y">
            <Button loading={loading} onClick={handleSubmit(onConfirm)}>
              Submit
            </Button>
          </div>
        </ModalBody>
      </form>
    </Modal>
  );
};

export default ModalVerificationRegister;
